const currencyFormatter = new Intl.NumberFormat("cs-CZ", {
	maximumFractionDigits: 0,
});

function compactFractionDigits(value: number): number {
	if (value < 10) return 3;
	if (value < 100) return 2;
	return 1;
}

export function formatCurrency(value: number | null | undefined): string {
	if (value == null) return "–";
	const abs = Math.abs(value);
	const sign = value < 0 ? "−" : "";

	if (abs >= 1_000_000_000) {
		const billions = abs / 1_000_000_000;
		return `${sign}${billions.toLocaleString("cs-CZ", { maximumFractionDigits: compactFractionDigits(billions) })} mld. Kč`;
	}
	if (abs >= 1_000_000) {
		const millions = abs / 1_000_000;
		return `${sign}${millions.toLocaleString("cs-CZ", { maximumFractionDigits: compactFractionDigits(millions) })} mil. Kč`;
	}
	if (abs >= 1_000) {
		return `${sign}${(abs / 1_000).toLocaleString("cs-CZ", { maximumFractionDigits: 0 })} tis. Kč`;
	}
	return `${sign}${currencyFormatter.format(abs)} Kč`;
}

export function formatDate(isoDate: string | null | undefined): string {
	if (!isoDate) return "–";
	return new Date(isoDate).toLocaleDateString("cs-CZ", {
		timeZone: "Europe/Prague",
		day: "numeric",
		month: "numeric",
		year: "numeric",
	});
}

/** Czech relative time for feeds: "před 5 min", "včera", falls back to a date. */
export function formatRelativeDate(isoDate: string | null | undefined): string {
	if (!isoDate) return "–";
	const diffMs = Date.now() - new Date(isoDate).getTime();
	const minutes = Math.floor(diffMs / 60_000);
	if (minutes < 1) return "právě teď";
	if (minutes < 60) return `před ${minutes} min`;
	const hours = Math.floor(minutes / 60);
	if (hours === 1) return "před hodinou";
	if (hours < 24) return `před ${hours} h`;
	const days = Math.floor(hours / 24);
	if (days === 1) return "včera";
	if (days < 7) return `před ${days} dny`;
	return formatDate(isoDate);
}

interface PersonName {
	firstName: string;
	lastName: string;
	titleBefore?: string | null;
	titleAfter?: string | null;
}

export function formatPercent(value: number | null | undefined): string {
	if (value == null) return "–";
	return `${(value * 100).toLocaleString("cs-CZ", { maximumFractionDigits: 1 })} %`;
}

export function formatRatio(value: number | null | undefined): string {
	if (value == null) return "–";
	return value.toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatNumber(value: number | null | undefined): string {
	if (value == null) return "–";
	return value.toLocaleString("cs-CZ", { maximumFractionDigits: 0 });
}

/**
 * Full (non-abbreviated) amount with currency — for registered capital,
 * deposits etc. where "200 tis. Kč" would be wrong. CZK renders as "Kč".
 */
export function formatAmountWithCurrency(
	value: number | null | undefined,
	currency: string | null | undefined,
): string {
	if (value == null) return "–";
	const formatted = value.toLocaleString("cs-CZ", { maximumFractionDigits: 2 });
	const unit = !currency || currency === "CZK" ? "Kč" : currency;
	return `${formatted} ${unit}`;
}

/**
 * Tender sources may publish an amount without its currency. Do not infer CZK
 * in that case because profile XML commonly omits the currency attribute.
 */
export function formatTenderAmount(
	value: number | null | undefined,
	currency: string | null | undefined,
): string {
	if (value == null) return "–";
	const normalizedCurrency = currency?.trim().toUpperCase();
	const formatted = value.toLocaleString("cs-CZ", { maximumFractionDigits: 2 });
	if (!normalizedCurrency) return `${formatted} (měna neuvedena)`;
	return formatAmountWithCurrency(value, normalizedCurrency);
}

interface BankAccountLike {
	type: "standard" | "nonstandard";
	prefix?: string;
	number: string;
	bankCode?: string;
}

/** Czech bank account display: `[prefix-]number/bankCode` for standard, raw number otherwise. */
export function formatBankAccount(account: BankAccountLike): string {
	if (account.type === "standard") {
		const prefix = account.prefix ? `${account.prefix}-` : "";
		const bankCode = account.bankCode ? `/${account.bankCode}` : "";
		return `${prefix}${account.number}${bankCode}`;
	}
	return account.number;
}

export function formatPersonName(person: PersonName): string {
	const parts: string[] = [];
	if (person.titleBefore) parts.push(person.titleBefore);
	parts.push(person.firstName);
	parts.push(person.lastName);
	if (person.titleAfter) parts.push(`, ${person.titleAfter}`);
	return parts.join(" ").replace(" , ", ", ");
}
